import { createStyles, withStyles } from '@mui/styles'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import React, { useEffect, useState } from 'react'
import { ColumnDescriptor, Select, TableItem, TextField } from '../../common/customComponents'
import { t } from '../../../infrastructure/i18nextHelper'
import * as api from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { applyFilters } from '../../common/filtersHelper'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { SnackbarContainer } from '../../../infrastructure/snackbars'

type PriceIndex = {
    id: Guid
    name: string
    longName: string
    currency: string
    unitOfMeasurement: string
    medecoCode: string
    medecoInternalCode: string
    type: 'Quotation' | 'RegulatedPrice'
    quantityPriceUnit: string
}

let emptyPriceIndex = (): PriceIndex => ({
    id: guid.empty,
    name: '',
    longName: '',
    currency: '',
    unitOfMeasurement: '',
    medecoCode: '',
    medecoInternalCode: '',
    type: 'Quotation',
    quantityPriceUnit: '-'
})

let toTableItem = (item: PriceIndex): TableItem<MasterDataItem<PriceIndex>> => ({
    id: item.id,
    name: item.name,
    longName: item.longName,
    currency: item.currency,
    unitOfMeasurement: item.unitOfMeasurement,
    medecoCode: item.medecoCode,
    medecoInternalCode: item.medecoInternalCode,
    type: item.type,
    quantityPriceUnit: item.quantityPriceUnit,
    isModified: false
})

type PriceIndexFilters = {
    name: string | null
    longName: string | null
    currency: string | null
    unitOfMeasurement: string | null
    medecoCode: string | null
    medecoInternalCode: string | null
    quantityPriceUnit: string | null
    type: string | null
}

let noFilter: PriceIndexFilters = {
    name: null,
    longName: null,
    currency: null,
    unitOfMeasurement: null,
    medecoCode: null,
    medecoInternalCode: null,
    quantityPriceUnit: null,
    type: null
}

function PriceIndexMasterdata({ classes }: MuiProps) {
    let [priceIndexs, setPriceIndexs] = useState<PriceIndex[]>([])
    let [filters, setFilters] = useState<PriceIndexFilters>(noFilter)

    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let snackbar = SnackbarContainer.useContainer()

    async function load() {
        try {
            let response = await api.get<PriceIndex[]>('masterdata/priceIndex')
            setPriceIndexs(response)
        } catch (error) {
            setPriceIndexs([])
        }
    }

    let getItems = () =>
        applyFilters(priceIndexs, filters).map(toTableItem)

    let tBase = 'admin.masterdata.priceIndex.'

    let onSaveHandler = async (item: PriceIndex) => {
        let hasEmptyField = Object.values(item).some(value => value === '');
        if (hasEmptyField) {
            snackbar.error(t(tBase + 'emptyField'))
            return false
        }
        await api.post('masterdata/priceIndex', item)
        await load()
        return true
    }

    let onDeleteHandler = async (ids: string[]) => {
        api.del('masterdata/priceIndex', { ids: ids })
        await load()
        return true
    }

    let onExportExcelHander = () => {
        excelGenerator.generate({
            filename: 'PriceIndexes.xlsx',
            sheets: [{ name: 'PriceIndexes', lines: createExcelLines(getItems(), columns) }]
        })
    }

    let isManager = hasClaim(Claims.MasterdataPriceIndexManager)

    useEffect(() => {
        load()
    }, [])

    let columns: ColumnDescriptor<TableItem<MasterDataItem<PriceIndex>>>[] = [
        {
            name: t(tBase + 'name'), value: x => x.name,
            columnFilter: {
                value: filters.name ?? '',
                onChange: (name: string) => setFilters({ ...filters, name })
            }
        },
        {
            name: t(tBase + 'longName'), value: x => x.longName,
            columnFilter: {
                value: filters.longName ?? '',
                onChange: (longName: string) => setFilters({ ...filters, longName })
            }
        },
        {
            name: t(tBase + 'currency'), value: x => x.currency,
            columnFilter: {
                value: filters.currency ?? '',
                onChange: (currency: string) => setFilters({ ...filters, currency })
            }
        },
        {
            name: t(tBase + 'unitOfMeasurement'), value: x => x.unitOfMeasurement,
            columnFilter: {
                value: filters.unitOfMeasurement ?? '',
                onChange: (unitOfMeasurement: string) => setFilters({ ...filters, unitOfMeasurement })
            }
        },
        {
            name: t(tBase + 'medecoCode'), value: x => x.medecoCode,
            columnFilter: {
                value: filters.medecoCode ?? '',
                onChange: (medecoCode: string) => setFilters({ ...filters, medecoCode })
            }
        },
        {
            name: t(tBase + 'medecoInternalCode'), value: x => x.medecoInternalCode,
            columnFilter: {
                value: filters.medecoInternalCode ?? '',
                onChange: (medecoInternalCode: string) => setFilters({ ...filters, medecoInternalCode })
            }
        },
        {
            name: t(tBase + 'type'), value: x => x.type,
            columnFilter: {
                value: filters.type ?? '',
                onChange: (type: string) => setFilters({ ...filters, type })
            }
        },
        {
            name: t(tBase + 'quantityPriceUnit'), value: x => x.quantityPriceUnit,
            columnFilter: {
                value: filters.quantityPriceUnit ?? '',
                onChange: (quantityPriceUnit: string) => setFilters({ ...filters, quantityPriceUnit })
            }
        }
    ]

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId="priceIndex-table"
                headerLabel={t(tBase + 'priceIndexes')}
                itemLabel={t(tBase + 'priceIndex')}
                isManager={isManager}
                columns={columns}
                items={getItems()}
                onSave={onSaveHandler}
                onNew={emptyPriceIndex}
                onDelete={onDeleteHandler}
                onExportExcel={onExportExcelHander}
            >{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <TextField label={t(tBase + 'name')}
                                disabled={!isManager}
                                text={selectedItem.name}
                                onChange={event => setSelectedItem({ ...selectedItem, name: event.target.value })} />
                            <TextField label={t(tBase + 'longName')}
                                disabled={!isManager}
                                text={selectedItem.longName}
                                onChange={event => setSelectedItem({ ...selectedItem, longName: event.target.value })} />
                            <TextField label={t(tBase + 'currency')}
                                disabled={!isManager}
                                text={selectedItem.currency}
                                onChange={event => setSelectedItem({ ...selectedItem, currency: event.target.value })} />
                            <TextField label={t(tBase + 'unitOfMeasurement')}
                                disabled={!isManager}
                                text={selectedItem.unitOfMeasurement}
                                onChange={event => setSelectedItem({ ...selectedItem, unitOfMeasurement: event.target.value })} />
                            <TextField label={t(tBase + 'medecoCode')}
                                disabled={!isManager}
                                text={selectedItem.medecoCode}
                                onChange={event => setSelectedItem({ ...selectedItem, medecoCode: event.target.value })} />
                            <TextField label={t(tBase + 'medecoInternalCode')}
                                disabled={!isManager}
                                text={selectedItem.medecoInternalCode}
                                onChange={event => setSelectedItem({ ...selectedItem, medecoInternalCode: event.target.value })} />
                            <Select label={t(tBase + 'type')}
                                disabled={!isManager}
                                value={selectedItem.type}
                                choices={['Quotation', 'RegulatedPrice'].map(x => ({ value: x, text: x }))}
                                onChange={value => setSelectedItem({ ...selectedItem, type: value })} />
                        </>
                    )
                }
            </MasterDataShell>
        </div>
    )
}

let styles = (theme) =>
    createStyles({
        container: { height: '100%' },
    })

export default withStyles(styles, muiOptions)(PriceIndexMasterdata)